/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, DraggablePositionType } from '@components/atoms'

import { PdfReaderLayout } from './PdfReaderLayout'
import { PdfToolbar } from './PdfToolbar'
import { SignatureInputModal } from './SignatureInputModal'
import { DraggableText } from './components/DraggableText'
import { DraggableSignature } from './components/DraggableSignature'
import { DraggableSignaturePosition } from './components/DraggableSignaturePosition'
import { DraggableSignTextPosition } from './components/DraggableSignTextPosition'
import { Document, Page, pdfjs } from 'react-pdf'

import config from '@configs'
import { FileType, ContractFormType, SignPositionOffsetType } from '@schemas'
import { PDFDocument, rgb } from 'pdf-lib'
import fontkit from '@pdf-lib/fontkit'
import dayjs from 'dayjs'
import { grey, blueGrey } from '@mui/material/colors'
import CheckPng from '@public/images/png/check.png'

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`

export interface SignPositionUnitType extends DraggablePositionType {
	page: number
	isSet: boolean
	show: boolean
	resX: number
	resY: number
	iconName: string
	title: string
}

export interface SignPositionType {
	mateName: SignPositionUnitType
	mateName2: SignPositionUnitType
	mateName3: SignPositionUnitType
	signedAt: SignPositionUnitType
	signedAt2: SignPositionUnitType
	signedAt3: SignPositionUnitType
	sign: SignPositionUnitType
	signedBy: SignPositionUnitType
	signedBy2: SignPositionUnitType
	signedBy3: SignPositionUnitType
	check: SignPositionUnitType
	businessNumber: SignPositionUnitType
	businessNumber2: SignPositionUnitType
	businessNumber3: SignPositionUnitType
	address: SignPositionUnitType
	address2: SignPositionUnitType
	address3: SignPositionUnitType
	guarantor: SignPositionUnitType
}

export type SignPositionTargetType =
	| 'mateName'
	| 'mateName2'
	| 'mateName3'
	| 'signedAt'
	| 'signedAt2'
	| 'signedAt3'
	| 'sign'
	| 'signedBy'
	| 'signedBy2'
	| 'signedBy3'
	| 'check'
	| 'businessNumber'
	| 'businessNumber2'
	| 'businessNumber3'
	| 'address'
	| 'address2'
	| 'address3'
	| 'guarantor'

interface PageDetailType {
	width: number
	height: number
	originalWidth: number
	originalHeight: number
}

interface PropsType {
	pdf: any
	mode?: 'none' | 'add' | 'update' | 'sign'
	showSignaturePosition?: boolean
	showSignature?: boolean
	showAddText?: boolean
	showAddDate?: boolean
	showReset?: boolean
	showDownload?: boolean
	onResetClick?: () => void
	positionOffset?: ContractFormType['signPosition']
	onSignPositionSetting?: () => void
	onSignSubmit?: (aFile: FileType) => void
	setPdfPage?: (aPage: number) => void
	signPosition?: SignPositionType
	onUpdateSignedPositionClick?: (params: {
		target: SignPositionTargetType
		page?: number
		x?: number
		y?: number
		offsetX?: number
		offsetY?: number
		resX?: number
		resY?: number
		isSet?: boolean
		show?: boolean
	}) => void
	isMobile?: boolean
}

export const defaultSignPositionUnit: SignPositionUnitType = {
	iconName: 'check',
	title: '',
	page: 0,
	x: 0,
	y: 0,
	offsetX: 0,
	offsetY: 0,
	resX: 0,
	resY: 0,
	isSet: false,
	show: false,
}

export const defaultSignPosition: SignPositionType = {
	sign: { ...defaultSignPositionUnit, iconName: 'pen', title: '서명*' },
	mateName: { ...defaultSignPositionUnit, iconName: 'store', title: '거래처명1' },
	mateName2: { ...defaultSignPositionUnit, iconName: 'store', title: '거래처명2' },
	mateName3: { ...defaultSignPositionUnit, iconName: 'store', title: '거래처명3' },
	signedAt: { ...defaultSignPositionUnit, iconName: 'calendar', title: '계약체결일1' },
	signedAt2: { ...defaultSignPositionUnit, iconName: 'calendar', title: '계약체결일2' },
	signedAt3: { ...defaultSignPositionUnit, iconName: 'calendar', title: '계약체결일3' },
	signedBy: {
		...defaultSignPositionUnit,
		iconName: 'account-tie',
		title: '대표자 이름1',
	},
	signedBy2: {
		...defaultSignPositionUnit,
		iconName: 'account-tie',
		title: '대표자 이름2',
	},
	signedBy3: {
		...defaultSignPositionUnit,
		iconName: 'account-tie',
		title: '대표자 이름3',
	},
	check: { ...defaultSignPositionUnit, iconName: 'check-circle', title: '확인 표시' },
	businessNumber: {
		...defaultSignPositionUnit,
		iconName: 'file-certificate',
		title: '사업자등록번호1',
	},
	businessNumber2: {
		...defaultSignPositionUnit,
		iconName: 'file-certificate',
		title: '사업자등록번호2',
	},
	businessNumber3: {
		...defaultSignPositionUnit,
		iconName: 'file-certificate',
		title: '사업자등록번호3',
	},
	address: { ...defaultSignPositionUnit, iconName: 'map-marker', title: '거래처 주소1' },
	address2: { ...defaultSignPositionUnit, iconName: 'map-marker', title: '거래처 주소2' },
	address3: { ...defaultSignPositionUnit, iconName: 'map-marker', title: '거래처 주소3' },
	guarantor: {
		...defaultSignPositionUnit,
		iconName: 'account-multiple-check',
		title: '보증인',
	},
}

export const defaultPageDetail = {
	width: 0,
	height: 0,
	originalWidth: 0,
	originalHeight: 0,
}

export const defaultPostion: DraggablePositionType = {
	x: 0,
	y: 0,
	offsetY: 0,
	offsetX: 0,
}

function downloadURI(uri: string, name: string) {
	const link = document.createElement('a')
	link.download = name
	link.href = uri
	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)
}

export const PdfReader = (props: PropsType) => {
	const apiEnv: string = config.get('api').env
	const apiHost: string = process.env[`REACT_APP_API_${apiEnv}_URL`] ?? ''
	const apiPort: string = config.get('api').port
	const apiUrl = `${apiHost}:${apiPort}`
	const mode = props.mode ?? 'none'
	const isMobile = props.isMobile ?? false

	// state
	const [pdf, setPdf] = React.useState<string>(props.pdf)
	const [signatureImageBase64, setSignatureImageBase64] = React.useState<any>(null)
	const [autoDate, setAutoDate] = React.useState<boolean>(true)
	const [showSignatureModal, setShowSignatureModal] = React.useState<boolean>(false)
	const [showTextInput, setShowTextInput] = React.useState<string>('none')
	const [position, setPosition] = React.useState<DraggablePositionType>(defaultPostion)
	const [totalPages, setTotalPages] = React.useState<number>(0)
	const [page, setPage] = React.useState<number>(0)
	const [pageDetails, setPageDetails] = React.useState<PageDetailType>(defaultPageDetail)
	const [scale, setScale] = React.useState<number>(100)
	const [isHandleMode, setIsHandleMode] = React.useState<boolean>(false)
	const [isSigned, setIsSigned] = React.useState<boolean>(false)
	const [isSignSaved, setIsSignSaved] = React.useState<boolean>(false)

	const defaultPositionTargetArr: SignPositionTargetType[] = [
		'mateName',
		'mateName2',
		'mateName3',
		'signedAt',
		'signedAt2',
		'signedAt3',
		'sign',
		'signedBy',
		'signedBy2',
		'signedBy3',
		'check',
		'businessNumber',
		'businessNumber2',
		'businessNumber3',
		'address',
		'address2',
		'address3',
		'guarantor',
	]
	const signPosition: SignPositionType = props.signPosition ?? defaultSignPosition
	const signPositionTargetArr = Object.keys(signPosition) as SignPositionTargetType[]

	// variables
	const showSignature: boolean =
		(props.showSignature && pdf !== '' && signatureImageBase64 !== '') ?? false
	const showAddText: boolean = (props.showAddText && pdf !== '') ?? false
	const showAddDate: boolean = (props.showAddDate && pdf !== '') ?? false
	const showReset: boolean = (props.showReset && pdf !== '') ?? false
	const showDownload: boolean = (props.showDownload && pdf !== '') ?? false
	const signatureOffsetX = 155
	const signatureOffsetY = 178

	// position offset variables
	const positionOffset: ContractFormType['signPosition'] = props.positionOffset ?? []

	const isAbleToSign: boolean = React.useMemo(() => {
		const idx = positionOffset.findIndex(aPosition => aPosition.id === 'sign')
		const signPosition: SignPositionOffsetType = positionOffset[idx]
		if (mode === 'sign' && signPosition.page === page) {
			return true
		} else {
			return false
		}
	}, [positionOffset, page])

	const viewerSize = React.useMemo(() => {
		return isMobile
			? {
					width: window.innerWidth - 34,
					height: window.innerHeight,
			  }
			: {
					width: 800,
					height: 1200,
			  }
	}, [isMobile])

	const showSignPosition = React.useMemo(() => {
		const defaultObj: { [key in SignPositionTargetType]: boolean } = {
			mateName: false,
			mateName2: false,
			mateName3: false,
			signedAt: false,
			signedAt2: false,
			signedAt3: false,
			sign: false,
			signedBy: false,
			signedBy2: false,
			signedBy3: false,
			check: false,
			businessNumber: false,
			businessNumber2: false,
			businessNumber3: false,
			address: false,
			address2: false,
			address3: false,
			guarantor: false,
		}

		const res: { [key in SignPositionTargetType]: boolean } =
			defaultPositionTargetArr.reduce((acc, aTarget) => {
				if (mode === 'add') {
					const idx = Object.keys(signPosition).findIndex(
						aTarget2 => aTarget2 === aTarget,
					)

					if (idx > -1) {
						acc[aTarget] = signPosition[aTarget].show
					} else {
						acc[aTarget] = false
					}
				} else if (mode === 'update' || mode === 'sign') {
					const idx = positionOffset.findIndex(aPostion => aPostion.id === aTarget)
					const positionPage = idx > -1 ? positionOffset[idx].page : -1
					const isSamePage = page === positionPage

					if (idx > -1 && isSamePage) {
						acc[aTarget] = true
					} else {
						acc[aTarget] = false
					}
				} else {
					acc[aTarget] = false
				}
				return acc
			}, defaultObj)

		return res
	}, [mode, positionOffset, signPosition, page])

	React.useEffect(() => {
		if (props.setPdfPage) {
			props.setPdfPage(page)
		}
	}, [page])

	//ref
	const documentRef = React.useRef<any>(null)

	React.useEffect(() => {
		if (typeof props.pdf === 'string') {
			const newPdf: string = !props.pdf.startsWith('data')
				? `data:application/pdf;base64,${props.pdf}`
				: props.pdf.replace('dataapplication/pdfbase64', 'data:application/pdf;base64,')
			setPdf(newPdf)
		} else {
			setPdf('')
		}
	}, [props.pdf])

	const blobToURL = async (aFile: File): Promise<FileType> => {
		return new Promise(resolve => {
			const reader = new FileReader()
			reader.readAsDataURL(aFile)
			reader.onloadend = () => {
				const res: FileType = {
					name: aFile.name ?? 'untitled.pdf',
					size: aFile.size,
					data: reader.result as string,
				}
				resolve(res)
			}
		})
	}

	const onPositionEnd = (e: any) => {
		const x: number = e.x
		const y: number = e.y
		const offsetX: number = e.offsetX
		const offsetY: number = e.offsetY

		setPosition({
			x: x,
			y: y,
			offsetX: offsetX,
			offsetY: offsetY,
		})
	}

	const onTextSet = async (text: string) => {
		const { originalHeight, originalWidth } = pageDetails
		const scale = originalWidth / documentRef.current.clientWidth

		const y =
			documentRef.current.clientHeight -
			(position.y + 12 * scale - position.offsetY - documentRef.current.offsetTop)
		const x = position.x - 166 - position.offsetX - documentRef.current.offsetLeft

		// new XY in relation to actual document size
		const newY = (y * originalHeight) / documentRef.current.clientHeight
		const newX = (x * originalWidth) / documentRef.current.clientWidth

		const pdfDoc = await PDFDocument.load(pdf)

		const pages = pdfDoc.getPages()
		const selectedPage = pages[page]

		selectedPage.drawText(text, {
			x: newX,
			y: newY,
			size: 20 * scale,
		})

		const pdfBytes: Uint8Array = await pdfDoc.save()
		const blob = new Blob([new Uint8Array(pdfBytes)])

		const newFile: FileType = await blobToURL(blob as File)
		setPdf(newFile.data)
		setPosition(defaultPostion)
		setShowTextInput('none')
	}

	const onSignatureSubmit = async (): Promise<void> => {
		const { originalWidth } = pageDetails

		const scale = originalWidth / documentRef.current.clientWidth

		if (props.positionOffset) {
			const url = `${apiUrl}/public/fonts/NanumGothic-Bold.ttf`
			const fontBytes = await fetch(url, { mode: 'cors' }).then(res => res.arrayBuffer())

			const pdfDoc = await PDFDocument.load(pdf)
			pdfDoc.registerFontkit(fontkit)
			const customFont = await pdfDoc.embedFont(fontBytes)

			for await (const aPosition of props.positionOffset) {
				const x: number =
					aPosition.id === 'sign'
						? aPosition.x
						: aPosition.id === 'address' ||
						  aPosition.id === 'address2' ||
						  aPosition.id === 'address3'
						? aPosition.x - 150
						: aPosition.id === 'check'
						? aPosition.x + 100
						: aPosition.x
				const y: number =
					aPosition.id === 'sign'
						? 960 - aPosition.y + 10
						: aPosition.id === 'check'
						? 960 - aPosition.y + 38
						: 960 - aPosition.y + 49

				// const newX = 470
				// const newY = 750
				const newX: number = Math.round(x / 1.28)
				const newY: number = Math.round(y / 1.28)

				const pages = pdfDoc.getPages()
				const selectedPage = pages[aPosition.page]

				if (aPosition.id === 'sign') {
					const pngImage = await pdfDoc.embedPng(signatureImageBase64)
					const pngDims = pngImage.scale(scale * 0.3)

					selectedPage.drawImage(pngImage, {
						x: newX,
						y: newY,
						width: pngDims.width,
						height: pngDims.height,
					})

					if (autoDate) {
						selectedPage.drawText(`서명일시: ${dayjs().format('YYYY-MM-DD HH:mm:ss')}`, {
							x: newX,
							y: newY - 10,
							size: 12 * scale,
							font: customFont,
							color: rgb(0.074, 0.545, 0.262),
						})
					}
				} else if (aPosition.id === 'check') {
					const pngImage = await pdfDoc.embedPng(CheckPng)
					const pngDims = pngImage.scale(scale * 0.1)

					selectedPage.drawImage(pngImage, {
						x: newX,
						y: newY,
						width: pngDims.width,
						height: pngDims.height,
					})
				} else {
					selectedPage.drawText(aPosition.value ?? '', {
						x: newX,
						y: newY,
						font: customFont,
						size: 16 * scale,
					})
				}
			}

			const pdfBytes: Uint8Array = await pdfDoc.save()
			const blob = new Blob([new Uint8Array(pdfBytes)])

			const newFile: FileType = await blobToURL(blob as File)

			setPdf(newFile.data)
			setIsSignSaved(true)
			setPosition(defaultPostion)
			setSignatureImageBase64(null)

			if (props.onSignSubmit) {
				props.onSignSubmit(newFile)
			}
		}
	}

	const onReadyToSign = () => {
		setPdf(props.pdf)
		setIsSignSaved(false)
		setIsSigned(false)
		setSignatureImageBase64(null)
		setShowSignatureModal(true)
	}

	const onSignatureButtonClick = () => {
		setShowSignatureModal(true)
	}

	const onAddDateButtonClick = () => {
		setShowTextInput('date')
	}

	const onAddTextButtonClick = () => {
		setShowTextInput('text')
	}

	const onDownloadButtonClick = () => {
		downloadURI(pdf, 'file.pdf')
	}

	const onResetButtonClick = () => {
		setShowTextInput('none')
		setShowSignatureModal(false)
		setSignatureImageBase64(null)
		setPdf('')
		setTotalPages(0)
		setPage(0)
		setPageDetails(defaultPageDetail)

		if (props.onResetClick) {
			props.onResetClick()
		}
	}

	const onZoomIn = () => {
		if (scale < 200) {
			const newScale = scale + 10
			setScale(newScale)
		}
	}

	const onZoomIOut = () => {
		if (scale > 20) {
			const newScale = scale - 10
			setScale(newScale)
		}
	}

	const onToggleHandleMode = () => {
		setIsHandleMode(!isHandleMode)
	}

	const onSignaturePositionSubmit = (target: SignPositionTargetType) => {
		if (signPosition) {
			const bodyOffsets = document.body.getBoundingClientRect()

			const scrollLeft: number = documentRef.current.scrollLeft
			const scrollTop: number = documentRef.current.scrollTop

			const x: number =
				signPosition[target].x -
				signPosition[target].offsetX -
				signatureOffsetX -
				(bodyOffsets.width - documentRef.current.scrollWidth) / 2 +
				scrollLeft +
				1
			const y: number =
				documentRef.current.scrollHeight -
				(signPosition[target].y - signPosition[target].offsetY + scrollTop) +
				signatureOffsetY -
				13

			// const resX = 600
			// const resY = 960
			const resX = x
			const resY = 960 - y

			onUpdateSignedPositionClick({
				target: target,
				page: page,
				resX: resX,
				resY: resY,
				isSet: true,
			})
		}
	}

	const onUpdateSignedPositionClick = (params: {
		target: SignPositionTargetType
		page?: number
		x?: number
		y?: number
		offsetX?: number
		offsetY?: number
		resX?: number
		resY?: number
		isSet?: boolean
		show?: boolean
	}) => {
		if (props.onUpdateSignedPositionClick) {
			props.onUpdateSignedPositionClick(params)
		}
	}

	const onSignModalClose = () => {
		setShowSignatureModal(false)
	}

	const onSignModalSubmit = (url: string) => {
		setSignatureImageBase64(url)
		setShowSignatureModal(false)
		setIsSigned(true)
	}

	const onSignPositionSetting = () => {
		onUpdateSignedPositionClick({
			target: 'sign',
			page: page,
			isSet: false,
		})
		if (props.onSignPositionSetting) {
			props.onSignPositionSetting()
		}
	}

	return (
		<View css={style}>
			{pdf !== '' && (
				<PdfReaderLayout
					isHandleMode={isHandleMode}
					toolbar={
						<PdfToolbar
							mode={mode}
							showAddText={showAddText}
							showAddDate={showAddDate}
							showReset={showReset}
							showDownload={showDownload}
							totalPages={totalPages}
							page={page}
							setPage={setPage}
							onSignatureClick={onSignatureButtonClick}
							onAddDateClick={onAddDateButtonClick}
							onAddTextClick={onAddTextButtonClick}
							onResetClick={onResetButtonClick}
							onDownloadClick={onDownloadButtonClick}
							onZoomIn={onZoomIn}
							onZoomIOut={onZoomIOut}
							scale={scale}
							isHandleMode={isHandleMode}
							isAbleToSign={isAbleToSign}
							isSigned={isSigned}
							onSignatureSubmit={onSignatureSubmit}
							onReadyToSign={onReadyToSign}
							onToggleHandleMode={onToggleHandleMode}
						/>
					}
					document={
						<View ref={documentRef} className="documentBlockView">
							{signPositionTargetArr.map(aTarget => {
								return (
									<React.Fragment key={aTarget}>
										{aTarget === 'sign' ? (
											<></>
										) : !isSignSaved && showSignPosition[aTarget] ? (
											<DraggableSignTextPosition
												mode={mode}
												onClose={() =>
													onUpdateSignedPositionClick({
														target: aTarget,
														show: false,
													})
												}
												onStop={(e: DraggablePositionType) =>
													onUpdateSignedPositionClick({
														target: aTarget,
														...e,
													})
												}
												onSubmit={() => onSignaturePositionSubmit(aTarget)}
												currentPage={page}
												positionTarget={aTarget}
												onSetting={() =>
													onUpdateSignedPositionClick({
														target: aTarget,
														page: page,
														isSet: false,
													})
												}
											/>
										) : null}
									</React.Fragment>
								)
							})}

							{!isSigned && showSignPosition.sign && (
								<DraggableSignaturePosition
									mode={mode}
									onStop={(e: DraggablePositionType) =>
										onUpdateSignedPositionClick({
											target: 'sign',
											...e,
										})
									}
									onSubmit={() => onSignaturePositionSubmit('sign')}
									onSetting={onSignPositionSetting}
									onClose={() =>
										onUpdateSignedPositionClick({
											target: 'sign',
											show: false,
										})
									}
									showSignature={showSignature}
									currentPage={page}
									onSignatureClick={onSignatureButtonClick}
								/>
							)}

							{showTextInput !== 'none' ? (
								<DraggableText
									initialText={
										showTextInput === 'date' ? dayjs().format('YYYY.MM.DD') : null
									}
									onClose={() => setShowTextInput('none')}
									onStop={onPositionEnd}
									onSubmit={onTextSet}
								/>
							) : null}
							{isSigned && !isSignSaved ? (
								<DraggableSignature
									url={signatureImageBase64}
									onClose={() => {
										setSignatureImageBase64(null)
									}}
									onSubmit={onSignatureSubmit}
									onStop={(e: DraggablePositionType) => {
										setPosition(e)
									}}
								/>
							) : null}

							<View className="documentView">
								<Document
									file={pdf}
									onLoadSuccess={data => {
										setTotalPages(data.numPages)
									}}
								>
									<Page
										pageNumber={page + 1}
										width={viewerSize.width}
										height={viewerSize.height}
										scale={scale / 100}
										renderTextLayer={false}
										renderAnnotationLayer={false}
										onLoadSuccess={(data: PageDetailType) => {
											setPageDetails(data)
										}}
									/>
								</Document>
							</View>
						</View>
					}
				/>
			)}

			{showSignatureModal ? (
				<SignatureInputModal
					showModal={showSignatureModal}
					autoDate={autoDate}
					setAutoDate={setAutoDate}
					onClose={onSignModalClose}
					onSubmit={onSignModalSubmit}
				/>
			) : null}
		</View>
	)
}

const style = css({
	width: '100%',
	height: '100%',

	'.documentBlockView': {
		position: 'relative',
		width: '100%',
		height: '100%',
		margin: 'auto',
		padding: '1rem 0.5rem',
		background: blueGrey[50],
		overflow: 'auto',

		'.documentView': {
			width: 'fit-content',
			margin: 'auto',
			boxShadow: `0px 0px 3px 1px ${grey[400]}`,

			'.react-pdf__Page': {
				backgroundColor: 'rgba(255, 255, 255, 0)!important',
			},
		},
	},
})
